import type { Practitioner } from '@medplum/fhirtypes';
import { validateProviderProfile } from './labOrderValidation';

/** Browser-local memory of the last ordering provider picked, so a Care Manager who always orders under the same NP isn't asked each time. */
export const LAST_ORDERING_PROVIDER_STORAGE_KEY = 'orbit.labOrder.lastOrderingProviderId';

/**
 * Practitioners who can stand as the ordering provider on a lab order: active, and carrying the
 * NPI Health Gorilla requires. Callers pass practitioners already scoped to
 * LAB_ORDERING_PROVIDER_ROLES.
 */
export function getEligibleOrderingProviders(practitioners: Practitioner[]): Practitioner[] {
  const byId = new Map<string, Practitioner>();
  for (const practitioner of practitioners) {
    if (!practitioner.id || practitioner.active === false) continue;
    if (!validateProviderProfile(practitioner).valid) continue;
    byId.set(practitioner.id, practitioner);
  }
  return [...byId.values()];
}

/**
 * The ordering provider to preselect. A provider who can order for themselves defaults to
 * themselves; anyone else gets whoever they picked last, if that provider is still eligible.
 * Otherwise nothing, and the form requires a choice.
 */
export function resolveDefaultOrderingProvider(
  currentUser: Practitioner | undefined,
  eligible: Practitioner[],
  lastUsedId: string | null | undefined
): Practitioner | undefined {
  const self = currentUser?.id ? eligible.find((p) => p.id === currentUser.id) : undefined;
  if (self) return self;
  if (lastUsedId) return eligible.find((p) => p.id === lastUsedId);
  return undefined;
}
